import { useState, useCallback } from 'react';
import { message } from 'antd';
import { pw2202Api, F2120, F2121 } from '../../../services/pw2202Api';
import { Mode } from './usePW2202Mode';
import { calcDetailAmount, calcTotals } from '../helpers';

export function usePW2202Record() {
  const [record, setRecord] = useState<F2120 | null>(null);
  const [formData, setFormData] = useState<Partial<F2120>>({});
  const [details, setDetails] = useState<Partial<F2121>[]>([]);
  const [selectedDetailRow, setSelectedDetailRow] = useState(0);
  const [loading, setLoading] = useState(false);

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string): Promise<{ data: F2120 } | null> => {
    setLoading(true);
    try {
      const data = await pw2202Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      setDetails(data.details || []);
      setSelectedDetailRow(0);
      return { data };
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof F2120, value: F2120[keyof F2120]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof F2120): F2120[keyof F2120] => {
    return (formData as Record<string, F2120[keyof F2120]>)[key] ?? '';
  }, [formData]);

  /** Save (create or update) [T-F01: use mutation response] */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: F2120) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as F2120,
        details: details as F2121[],
      };
      if (mode === 'add') {
        const result = await pw2202Api.create(req);
        message.success(`新增成功: ${result.f2120_01}`);
        onSuccess(result);
      } else {
        const result = await pw2202Api.update(formData.f2120_01!, req);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        setDetails(result.details || []);
        onSuccess(result);
      }
    } catch (e: unknown) {
      // [T-F06: join 422 errors for display]
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        const summary = errors.slice(0, 5).map(er => er.message).join('\n');
        const extra = errors.length > 5 ? `\n...共 ${errors.length} 項` : '';
        message.error({ content: summary + extra, duration: 6, style: { whiteSpace: 'pre-line' } });
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await pw2202Api.delete(record.f2120_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Add a detail row (max 99) */
  const addDetailRow = useCallback(() => {
    const maxSeq = details.reduce((max, d) => Math.max(max, d.f2121_02 || 0), 0);
    if (maxSeq >= 99) {
      message.warning('明細最多 99 項');
      return;
    }
    setDetails(prev => [...prev, {
      f2121_01: formData.f2120_01 || '',
      f2121_02: maxSeq + 1,
      f2121_07: (formData.f2120_07 as string) || '',
      f2121_11: '',
      f2121_11a: '',
      f2121_12: '',
      f2121_13: 0,
      f2121_14: 0,
      f2121_15: 0,
      f2121_18: '',
      f2121_21: '',
      f2121_22: 0,
    }]);
  }, [details, formData.f2120_01, formData.f2120_07]);

  /** Delete the selected detail row [T-F05: reindex] */
  const deleteDetailRow = useCallback(() => {
    if (details.length <= 0) return;
    setDetails(prev => {
      const next = prev.filter((_, i) => i !== selectedDetailRow);
      return next.map((d, i) => ({ ...d, f2121_02: i + 1 }));
    });
    setSelectedDetailRow(Math.max(0, selectedDetailRow - 1));
  }, [details, selectedDetailRow]);

  /** Update a single detail field with auto-calc */
  const setDetailField = useCallback((rowIdx: number, key: keyof F2121, value: F2121[keyof F2121]) => {
    setDetails(prev => {
      const next = [...prev];
      const row = { ...next[rowIdx], [key]: value };
      // Auto-calculate amount when qty or price changes
      if (key === 'f2121_13' || key === 'f2121_14') {
        const qty = key === 'f2121_13' ? (value as number) : (row.f2121_13 || 0);
        const price = key === 'f2121_14' ? (value as number) : (row.f2121_14 || 0);
        row.f2121_15 = calcDetailAmount(qty, price);
      }
      next[rowIdx] = row;
      return next;
    });
  }, []);

  /** Recalculate master totals from current details + formData */
  const recalcMasterTotals = useCallback(() => {
    const detailSum = details.reduce((sum, d) => sum + (d.f2121_15 || 0), 0);
    const transportation = (formData.f2120_23 as number) || 0;
    const freight = (formData.f2120_25 as number) || 0;
    const taxType = (formData.f2120_18 as number) || 2;
    const { subtotal, tax, grandTotal } = calcTotals(detailSum, transportation, freight, taxType);
    setFormData(prev => ({
      ...prev,
      f2120_17: detailSum,
      f2120_16: details.length,
      f2120_28: subtotal,
      f2120_19: tax,
      f2120_20: grandTotal,
    }));
  }, [details, formData.f2120_23, formData.f2120_25, formData.f2120_18]);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record]);

  /** Reset form for a new add (defaults) */
  const resetForAdd = useCallback(() => {
    setRecord(null);
    setFormData({
      f2120_80: 'N',
      f2120_18: 2,  // default tax-free
      f2120_17: 0,
      f2120_19: 0,
      f2120_20: 0,
      f2120_21: 0,
      f2120_23: 0,
      f2120_25: 0,
      f2120_28: 0,
      f2120_31: ' ',
    });
    setDetails([]);
    setSelectedDetailRow(0);
  }, []);

  /** Apply result after save [T-F01] */
  const applyResult = useCallback((result: F2120) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  return {
    record, formData, details,
    selectedDetailRow, loading,
    setSelectedDetailRow,
    setDetails,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    setDetailField, recalcMasterTotals,
    revertToRecord, resetForAdd, applyResult,
  };
}
